import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Sofa, Truck, Wrench, Trash2, FileDown } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import CartDrawer from "@/components/site/CartDrawer";
import FurnitureDepotQuote from "@/components/site/FurnitureDepotQuote";
import FurnitureDepotTerms from "@/components/site/FurnitureDepotTerms";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { icon: Truck, title: "We pick it up", body: "Straight from the store's dock to your door — you never lift a thing." },
  { icon: Wrench, title: "We assemble it", body: "Beds, dressers, sectionals, dining sets — built and placed where you want them." },
  { icon: Trash2, title: "We haul the old", body: "Check off the old pieces you want gone and they're out within 24 hours." },
];

export default function FurnitureDepotPage() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { axios.get(`${API}/furniture-depot/config`).then(({ data }) => setCfg(data)).catch(() => {}); }, []);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="furniture-depot-page">
      <Header />
      <main className="pt-20">
        <section className="bg-[#0C2E2B] text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-[1.2fr_1fr] gap-10 items-end">
            <div>
              <Link to="/" className="inline-flex items-center gap-1 text-xs font-bold text-white/70 hover:text-white mb-6" data-testid="fd-back-home"><ArrowLeft className="w-3.5 h-3.5" /> Back home</Link>
              <div className="font-tactical text-[11px] uppercase tracking-[0.3em] text-[#FFDF00] flex items-center gap-2"><Sofa className="w-4 h-4" /> Furniture Depot · Partner store delivery</div>
              <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mt-3">Bought it at the store?<br /><span className="text-[#FFDF00]">We deliver &amp; build it.</span></h1>
              <p className="text-base md:text-lg text-white/80 mt-5 max-w-xl">One flat rate for delivery and in-home assembly from our partner furniture stores. Get your exact price below — no photo needed — and lock your slot in a minute.</p>
            </div>
            <div className="grid gap-3">
              {STEPS.map((s) => (
                <div key={s.title} className="flex gap-3 bg-white/5 border border-white/10 rounded-xl p-4">
                  <s.icon className="w-6 h-6 text-[#FFDF00] shrink-0" />
                  <div><div className="font-bold">{s.title}</div><div className="text-sm text-white/70">{s.body}</div></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" id="quote">
          <div className="font-tactical text-[11px] uppercase tracking-[0.25em] text-[#0C2E2B]/60 mb-2">Instant quote</div>
          <h2 className="font-display font-black text-2xl sm:text-3xl text-[#0C2E2B] mb-6">Build your delivery</h2>
          <FurnitureDepotQuote />
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" id="terms">
          <div className="bg-white rounded-2xl border-2 border-zinc-200 p-6 sm:p-8">
            <div className="font-tactical text-[11px] uppercase tracking-[0.25em] text-[#0C2E2B]/60 mb-2">The fine print</div>
            <h2 className="font-display font-black text-2xl sm:text-3xl text-[#0C2E2B] mb-5" data-testid="fd-terms-title">Delivery, Assembly &amp; Disposal Terms</h2>
            <FurnitureDepotTerms terms={cfg?.terms} />
            <p className="text-xs text-zinc-500 mt-5">These terms apply alongside our general <Link to="/terms" className="underline font-bold">Terms of Service</Link>. Harvey's Place LLC.</p>
            <a href={`${API}/partner-terms.pdf`} target="_blank" rel="noreferrer" data-testid="fd-partner-pdf-btn"
              className="inline-flex items-center gap-2 mt-4 bg-[#0C2E2B] text-white text-xs font-black uppercase tracking-wider px-4 py-2.5 rounded-md hover:bg-[#0a2523] transition-colors">
              <FileDown className="w-4 h-4" /> Store owners: download our partner fee schedule &amp; terms (PDF)
            </a>
          </div>
        </section>
      </main>
      <Footer />
      <CartDrawer />
    </div>
  );
}
